import { supabase } from "@/integrations/supabase/client";

export type WpResource = "properties" | "claims";
export type WpAction = "list" | "get" | "approve" | "delete";

export interface WpListResponse<T = any> {
  total: number;
  items: T[];
}

export interface WpItemResponse<T = any> {
  item: T;
}

export interface WpErrorDetails {
  message: string;
  code?: string;
  status?: number;
  isWordPressError: boolean;
  isRetryable: boolean;
  rawDetails?: any;
}

export class WpSubmissionError extends Error {
  public readonly code?: string;
  public readonly status?: number;
  public readonly isWordPressError: boolean;
  public readonly isRetryable: boolean;
  public readonly rawDetails?: any;

  constructor(details: WpErrorDetails) {
    super(details.message);
    this.name = "WpSubmissionError";
    this.code = details.code;
    this.status = details.status;
    this.isWordPressError = details.isWordPressError;
    this.isRetryable = details.isRetryable;
    this.rawDetails = details.rawDetails;
  }
}

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
};

function isRetryableError(status?: number, code?: string): boolean {
  // Retry on network errors, timeouts, and server errors (5xx)
  if (!status) return true; // Network error, no status
  if (status >= 500 && status < 600) return true; // Server errors
  if (status === 429) return true; // Rate limited
  if (code === "internal_server_error") return true;
  return false;
}

function parseWpError(data: any, fallbackMessage: string): WpSubmissionError {
  // Check for WordPress REST API error structure
  if (data?.error) {
    const errorMsg = typeof data.error === "string" ? data.error : data.error?.message;
    const details = data.details;
    
    // WordPress REST API error structure
    if (details?.code) {
      let friendlyMessage = errorMsg || fallbackMessage;
      let status = details.data?.status || data.status;
      
      // Provide user-friendly messages for common WordPress errors
      switch (details.code) {
        case "rest_forbidden":
          friendlyMessage = "WordPress authentication failed. Check that API credentials have proper permissions.";
          break;
        case "rest_no_route":
          friendlyMessage = "WordPress API endpoint not found. The TPS Submissions plugin may not be installed or activated.";
          break;
        case "internal_server_error":
          friendlyMessage = "WordPress server error. Check the WordPress server logs for details.";
          // Extract specific PHP error info if available
          if (details.data?.error?.message) {
            const phpError = details.data.error.message;
            if (phpError.includes("RedisException")) {
              friendlyMessage = "WordPress Redis cache connection failed. Contact your WordPress administrator.";
            }
          }
          break;
        default:
          friendlyMessage = details.message || errorMsg || fallbackMessage;
      }
      
      return new WpSubmissionError({
        message: friendlyMessage,
        code: details.code,
        status,
        isWordPressError: true,
        isRetryable: isRetryableError(status, details.code),
        rawDetails: details,
      });
    }
    
    return new WpSubmissionError({
      message: errorMsg || fallbackMessage,
      isWordPressError: true,
      isRetryable: false,
      rawDetails: data,
    });
  }
  
  return new WpSubmissionError({
    message: fallbackMessage,
    isWordPressError: false,
    isRetryable: true, // Network errors are usually retryable
    rawDetails: data,
  });
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getBackoffDelay(attempt: number): number {
  // Exponential backoff with jitter
  const exponentialDelay = RETRY_CONFIG.baseDelayMs * Math.pow(2, attempt);
  const jitter = Math.random() * 0.3 * exponentialDelay; // 0-30% jitter
  return Math.min(exponentialDelay + jitter, RETRY_CONFIG.maxDelayMs);
}

async function invokeWpFunction(
  body: Record<string, any>,
  actionDescription: string,
  options: { retries?: boolean } = { retries: true }
): Promise<any> {
  let lastError: WpSubmissionError | null = null;
  const maxAttempts = options.retries ? RETRY_CONFIG.maxRetries : 1;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const { data, error } = await supabase.functions.invoke("wp-submissions", { body });

      // Handle Supabase function invocation errors
      if (error) {
        // Check if the error body has WordPress error details
        if (error.context && typeof error.context === "object") {
          throw parseWpError(error.context, `${actionDescription} failed`);
        }
        throw new WpSubmissionError({
          message: error.message || `${actionDescription} failed`,
          isWordPressError: false,
          isRetryable: true,
          rawDetails: error,
        });
      }

      // Check if data contains an error response from our edge function
      if (data?.error) {
        throw parseWpError(data, `${actionDescription} failed`);
      }

      return data;
    } catch (e) {
      if (e instanceof WpSubmissionError) {
        lastError = e;
        
        // Only retry if it's a retryable error and we have attempts left
        if (e.isRetryable && attempt < maxAttempts - 1) {
          const delay = getBackoffDelay(attempt);
          console.log(`Retrying ${actionDescription} in ${Math.round(delay)}ms (attempt ${attempt + 1}/${maxAttempts})`);
          await sleep(delay);
          continue;
        }
      } else {
        // Unknown error, wrap it
        lastError = new WpSubmissionError({
          message: e instanceof Error ? e.message : String(e),
          isWordPressError: false,
          isRetryable: false,
          rawDetails: e,
        });
      }
      break;
    }
  }

  throw lastError || new WpSubmissionError({
    message: `${actionDescription} failed after ${maxAttempts} attempts`,
    isWordPressError: false,
    isRetryable: false,
  });
}

export async function wpTestConnection(): Promise<{ success: boolean; message: string; details?: any }> {
  try {
    // Try to list with count only - minimal data transfer
    const data = await invokeWpFunction(
      { resource: "properties", action: "list", page: 0, perPage: 1, count: true },
      "Connection test",
      { retries: false } // Don't retry for connection test
    );
    
    return {
      success: true,
      message: `Connected successfully. Found ${data.total ?? 0} pending property submissions.`,
      details: data,
    };
  } catch (e) {
    if (e instanceof WpSubmissionError) {
      return {
        success: false,
        message: e.message,
        details: {
          code: e.code,
          status: e.status,
          isWordPressError: e.isWordPressError,
        },
      };
    }
    return {
      success: false,
      message: e instanceof Error ? e.message : String(e),
    };
  }
}

export async function wpList<T = any>(
  resource: WpResource,
  opts: { page?: number; perPage?: number } = {},
): Promise<WpListResponse<T>> {
  const data = await invokeWpFunction(
    { resource, action: "list", page: opts.page ?? 0, perPage: opts.perPage ?? 100 },
    `Loading ${resource}`
  );
  return data as WpListResponse<T>;
}

export async function wpGet<T = any>(resource: WpResource, id: string | number): Promise<WpItemResponse<T>> {
  const data = await invokeWpFunction(
    { resource, action: "get", id },
    `Fetching ${resource} details`
  );
  return data as WpItemResponse<T>;
}

export async function wpApprove<T = any>(resource: WpResource, id: string | number): Promise<WpItemResponse<T>> {
  const data = await invokeWpFunction(
    { resource, action: "approve", id },
    `Approving ${resource}`
  );
  return data as WpItemResponse<T>;
}

export async function wpDelete(resource: WpResource, id: string | number): Promise<{ ok: boolean } & Record<string, any>> {
  const data = await invokeWpFunction(
    { resource, action: "delete", id },
    `Deleting ${resource}`
  );
  return data as any;
}

export function pick<T extends Record<string, any>>(obj: T, keys: string[]) {
  const out: Record<string, any> = {};
  keys.forEach((k) => {
    if (obj && k in obj) out[k] = (obj as any)[k];
  });
  return out;
}

export function formatWpError(error: unknown): { title: string; description: string; isWpError: boolean; isRetryable: boolean } {
  if (error instanceof WpSubmissionError) {
    return {
      title: error.isWordPressError ? "WordPress Error" : "Connection Error",
      description: error.message,
      isWpError: error.isWordPressError,
      isRetryable: error.isRetryable,
    };
  }
  
  if (error instanceof Error) {
    return {
      title: "Error",
      description: error.message,
      isWpError: false,
      isRetryable: false,
    };
  }
  
  return {
    title: "Unknown Error",
    description: String(error),
    isWpError: false,
    isRetryable: false,
  };
}
